import { useState, useEffect, useRef } from 'react';
import { View, TouchableOpacity, Alert, ActivityIndicator, Platform, AppState, Share } from 'react-native';
import { Text } from '@/components';
import TrailMap from '@/components/TrailMap';
import { processBGLocation, makeMotionHandler } from '@/utils/bgHandlers';
import * as Haptics from 'expo-haptics';
import BackgroundGeolocation, { Location as BGLocation } from 'react-native-background-geolocation';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useNavigation } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { MapPin, Navigation, Camera } from 'lucide-react-native';
import styles from './record.styles';
import theme from '@/constants/colors';
import RecordOverlay from '@/components/RecordOverlay';
import { useSaveTrail } from '@/lib/queries/trails';
import { fetchTrailDetail } from '@/lib/api/trails';
import { useAuth } from '@/context/AuthContext';
import { Coordinate, Trail, TrailPoint } from '@/types/trail';
import { calculateTotalDistance } from '@/utils/distance';
import { appendCoordinateToPath } from '@/utils/backgroundTracking';
import { initBackgroundTracking } from '@/utils/backgroundGeolocationInit';
import { clearRecordingSession, saveBackup, RECORDING_STORAGE_KEYS } from '@/utils/recordingSession';
import { diag, diagInc, collectDiagnosticDump } from '@/utils/diagnostics';
import { captureAndStoreRecordingPhoto, requestBgPermissionAndInitialLocation, resolveInitialRecordingCoordinate } from '@/utils/recordingFlow';
import { loadRecordingState as loadRecordingStateHelper, requestPermissions as requestPermissionsHelper, startRecordingShared, stopRecordingShared } from '@/utils/recordingHelpers';
import { computePace, makeRecenterMapFactory, usePersistCoordinates, usePollingPosition, useSignalWatchdog } from '@/utils/recordingCommon';
import { LocationWatchdog } from '@/components/LocationWatchdog';
import { checkBackgroundPermissionStatus } from '@/utils/permissions';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LottieLoader from '@/components/LottieLoader';
import { saveEndWalkDraft } from '@/utils/endWalkDraft';
import { logEvent } from '@/lib/analytics';

// Background task removed — the native SDK will deliver locations via its own listeners

export default function RecordScreen({ trail: incomingTrail }: { trail?: Trail } = {}) {
  const { t } = useTranslation();
  const { session, user } = useAuth();
  const saveTrailMutation = useSaveTrail(session?.accessToken, user?.id);
  const token = session?.accessToken;
  const params = useLocalSearchParams();
  const router = useRouter();
  const navigation = useNavigation();
  const trailId = typeof params.trailId === 'string' ? params.trailId : undefined;
  let paramTrail: Trail | undefined = undefined;
  if (typeof params.trail === 'string') {
    try {
      paramTrail = JSON.parse(decodeURIComponent(params.trail));
    } catch (err) {
      console.warn('Failed to parse trail param:', err);
    }
  }

  const [initialTrail, setInitialTrail] = useState<Trail | undefined>(incomingTrail ?? paramTrail ?? undefined);
  const mapRef = useRef<any>(null);
  const [isRecording, setIsRecording] = useState<boolean>(false);

  // Hide the bottom tabs while recording so the map gets the full screen.
  useEffect(() => {
    (navigation as any).setOptions?.({ tabBarStyle: { display: isRecording ? 'none' : 'flex' } });
    return () => (navigation as any).setOptions?.({ tabBarStyle: { display: 'flex' } });
  }, [isRecording, navigation]);
  const [coordinates, setCoordinates] = useState<TrailPoint[]>([]);
  const coordinatesRef = useRef<TrailPoint[]>([]);
  const [currentLocation, setCurrentLocation] = useState<Coordinate | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isLoadingPermission, setIsLoadingPermission] = useState<boolean>(true);
  const [startLocation, setStartLocation] = useState<{ city?: string; country?: string } | null>(null);
  const locationSubscription = useRef<any>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const insets = useSafeAreaInsets();
  const [elevation, setElevation] = useState<number>(0);
  const [maxElevation, setMaxElevation] = useState<number>(0);
  const [pace, setPace] = useState<string>('0:00');
  const [speed, setSpeed] = useState<number>(0);
  const [maxSpeed, setMaxSpeed] = useState<number>(0);
  const [accuracy, setAccuracy] = useState<number | undefined>(undefined);
  const [showGpsCta, setShowGpsCta] = useState(false);
  const gpsCtaShownRef = useRef(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [sniffTime, setSniffTime] = useState<number>(0);
  const sniffTimeRef = useRef<number>(0);

  const stopStartTimeRef = useRef<number | null>(null);
  const lastLocationTimestamp = useRef<number>(Date.now());
  const lastAcceptedGpsTimestamp = useRef<number>(0);

  const bgReady = useRef<boolean>(false);
  const isRecordingRef = useRef<boolean>(isRecording);
  const recordingStartRef = useRef<number | null>(null);
  const maxElevationRef = useRef<number>(0);
  const maxSpeedRef = useRef<number>(0);

  // Backup throttling: save every N points or every interval, and on app state changes
  const POINT_THRESHOLD = 20; // save after this many new points
  const BACKUP_INTERVAL_MS = 30000; // 30s interval
  const lastBackupPointsRef = useRef<number>(0);
  const lastBackupTimeRef = useRef<number>(Date.now());

  // Web is browse-only: direct deep links to recording screens bounce out.
  useEffect(() => {
    if (Platform.OS === 'web') router.replace('/');
  }, [router]);

  // Keep refs synchronized with state so background handlers read fresh values
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);
  useEffect(() => { coordinatesRef.current = coordinates; }, [coordinates]);
  useEffect(() => { maxElevationRef.current = maxElevation; }, [maxElevation]);
  useEffect(() => { maxSpeedRef.current = maxSpeed; }, [maxSpeed]);
  // NOTE: sniffTimeRef is NOT synced from sniffTime state.  sniffTimeRef
  // tracks "accumulated completed sniff periods" only (written by
  // processBGLocation).  The timer computes the display value as
  // sniffTimeRef.current + ongoing-period.  If we synced the display
  // value back, the ongoing period would be double-counted each tick.

  useEffect(() => {
    // initialize background-geolocation SDK using shared handlers
    let mounted = true;

    const initBG = async () => {
      try {
        await initBackgroundTracking({
          onProviderChange: (event) => {
            diag('provider_change', String(event.status));
            if (
              event.status !== BackgroundGeolocation.AuthorizationStatus.Always &&
              event.status !== BackgroundGeolocation.AuthorizationStatus.WhenInUse
            ) {
              setAccuracy(undefined);
            }
          },
          onHeartbeat: (event) => {
            // Heartbeat confirms the background service is still alive.
            console.log('[BG] heartbeat received, service alive');
            diag('heartbeat');
          },
          onEnabledChange: (enabled) => {
            diag('enabled_change', String(enabled));
            // Detect if the background service is unexpectedly stopped
            // (common on Samsung/Xiaomi when battery optimisation kills the app)
            if (!enabled && isRecordingRef.current) {
              console.warn('[BG] tracking service was disabled while recording - OEM likely killed it');
              // Attempt to restart the service
              BackgroundGeolocation.start().then(() => {
                BackgroundGeolocation.changePace(true);
                console.log('[BG] tracking service restarted after OEM kill');
              }).catch((e: any) => {
                console.error('[BG] failed to restart tracking after OEM kill:', e);
              });
            }
          },
          onPowerSaveChange: (isPowerSave: boolean) => {
            // Power-save mode on Samsung/others can throttle or kill background services
            console.log('[BG] power save mode changed:', isPowerSave);
          },
          onLocation: (location: BGLocation) => processBGLocation(location, {
            isRecordingRef,
            setCurrentLocation,
            setAccuracy,
            setCoordinates,
            coordinatesRef,
            lastAcceptedGpsTimestamp,
            lastLocationTimestamp,
            setElevation,
            maxElevationRef,
            setMaxElevation,
            setSpeed,
            maxSpeedRef,
            setMaxSpeed,
            saveBackup,
            lastBackupPointsRef,
            lastBackupTimeRef,
            stopStartTimeRef,
            sniffTimeRef,
            setSniffTime,
          }),
          onLocationError: (err) => {
            console.warn('BG onLocation error', err);
            diag('loc_err', String(err));
            diagInc('onLocationErrors');
            setAccuracy(undefined);
          },
          onMotionChange: makeMotionHandler({
            isRecordingRef,
            setCurrentLocation,
            setAccuracy,
            setCoordinates,
            coordinatesRef,
            lastAcceptedGpsTimestamp,
            lastLocationTimestamp,
            setElevation,
            maxElevationRef,
            setMaxElevation,
            setSpeed,
            maxSpeedRef,
            setMaxSpeed,
            saveBackup,
            lastBackupPointsRef,
            lastBackupTimeRef,
            stopStartTimeRef,
            sniffTimeRef,
            setSniffTime,
          }),
        });

        if (!mounted) return;
        bgReady.current = true;
      } catch (err) {
        bgReady.current = false;
      }
    };

    initBG();

    return () => { mounted = false; BackgroundGeolocation.removeListeners(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One UI can silently kill the location FGS while the app is backgrounded
  // (onEnabledChange does NOT fire for OS kills). Reconcile when the user
  // returns to the app mid-walk: restart tracking if the service died.
  useEffect(() => {
    const sub = AppState.addEventListener('change', (s) => {
      if (s !== 'active' || !isRecordingRef.current || !bgReady.current) return;
      (async () => {
        try {
          const st = await BackgroundGeolocation.getState();
          if (!st.enabled) {
            diag('fg_service_dead_restart');
            await BackgroundGeolocation.start();
            await BackgroundGeolocation.changePace(true);
            diag('fg_service_restart_ok');
          } else {
            diag('fg_service_alive');
          }
        } catch (e) {
          diag('fg_reconcile_error', String((e as any)?.message ?? e));
        }
      })();
    });
    return () => sub.remove();
  }, []);

  // Throttled backups: save when enough new points accumulated
  useEffect(() => {
    if (!isRecordingRef.current) return;
    const len = coordinates.length;
    if (len === 0) return;
    if ((len - lastBackupPointsRef.current) >= POINT_THRESHOLD) {
      saveBackup(coordinates, sniffTimeRef.current);
      lastBackupPointsRef.current = len;
      lastBackupTimeRef.current = Date.now();
    }
    // only depend on coordinates so we evaluate when points added
  }, [coordinates]);

  // Persist coordinates to storage on every change (same logic used in FollowScreen)
  usePersistCoordinates(isRecording, coordinates);
  // Some devices / SDK configs only emit `onLocation` events intermittently;
  // `getCurrentPosition` often returns fresher readings. Use it as a supplement
  // while recording to ensure the UI and path stay in sync.
  usePollingPosition({ isRecording, bgReadyRef: bgReady, isRecordingRef, setCurrentLocation, setAccuracy, setCoordinates, coordinatesRef });

  // (AppState listener removed — persistence handled on-change and via backups)

  useEffect(() => {
    // Use shared background permission check to avoid duplicating logic and
    // to wait for the background SDK to initialize before deciding state.
    (async () => {
      diag('record_mount');
      try {
        const ok = await checkBackgroundPermissionStatus(bgReady);
        diag('bg_perm_check', `ready=${bgReady.current} ok=${ok}`);
        setHasPermission(ok);
      } catch (e) {
        console.warn('checkBackgroundPermissionStatus failed:', e);
        setHasPermission(false);
      } finally {
        setIsLoadingPermission(false);
        // still call requestPermissions to let user actively prompt if needed
        requestPermissions();
        loadRecordingState();
      }
    })();

    return () => {
      if (locationSubscription.current) {
        locationSubscription.current.remove();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- THE SIGNAL WATCHDOG & AUTO-RESUME ---
  useSignalWatchdog({ isRecording, lastLocationTimestampRef: lastLocationTimestamp, bgReadyRef: bgReady, setAccuracy, setCurrentLocation, coordinatesRef });

  useEffect(() => {
    if (!trailId && !incomingTrail && typeof params.trail !== 'string' && !isRecording) {
      setInitialTrail(undefined);
    }
  }, [trailId, incomingTrail, params.trail, isRecording]);

  useEffect(() => {
    if (initialTrail) {
      // no-op: initialTrail is used to seed map path only in RecordScreen
    }
  }, [initialTrail]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!initialTrail && trailId) {
        try {
          const t = await fetchTrailDetail(trailId, token);
          if (mounted && t) setInitialTrail(t);
        } catch (err) {
          console.warn('Failed to load trail by id in FollowScreen:', err);
        }
      }
    };
    load();
    return () => { mounted = false; };
  }, [initialTrail, trailId, token]);

  // followMode removed: RecordScreen relies on isRecording to control follow behavior

  const loadRecordingState = async () => {
    // delegate to shared helper
    await loadRecordingStateHelper({ setCoordinates, setIsRecording, setDuration, setMaxElevation, setMaxSpeed, setSniffTime });
  };

  const requestPermissions = async () => {
    // use shared helper
    await requestPermissionsHelper({ bgReadyRef: bgReady, setHasPermission, setIsLoadingPermission, setCurrentLocation, requestBgPermissionAndInitialLocation });
  };

  const startRecording = async () => {
    // Battery-optimisation guidance runs POST-start inside startRecordingShared
    // (fire-and-forget): awaiting OEM native calls before starting silently
    // dead-ended the Start button on Samsung One UI firmware (Android 17/S25) —
    // the tap did nothing: no alert, no recording. Never block the walk start.
    await doStartRecording();
  };

  const doStartRecording = async () => {
    diag('do_start_recording');
    const failReason = await startRecordingShared({ setIsRecording, setCoordinates, setDuration, setMaxElevation, setMaxSpeed, setCurrentLocation, bgReadyRef: bgReady, resolveInitialRecordingCoordinate, timerRef, setStartLocation, checkBattery: true, walkEventProps: { follow_mode: !!initialTrail } });
    if (failReason) {
      // System Alerts have proven unreliable on some OEM firmware — surface
      // the failure as a plain tappable banner (tap = share diagnostics).
      diag('start_fail_banner', failReason);
      setStartError(failReason);
      return;
    }
    setStartError(null);
    sniffTimeRef.current = 0;
    setSniffTime(0);
    stopStartTimeRef.current = null;

    // Replace the generic timer from startRecordingShared with one that
    // also tracks sniff time in real-time for the UI.  The timer reads
    // stopStartTimeRef (set by processBGLocation's speed-based sniff
    // detection) to show current sniff duration while the dog is
    // stationary.
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = setInterval(() => {
      // Duration from persisted start time
      AsyncStorage.getItem(RECORDING_STORAGE_KEYS.START_TIME).then((startTimeStr) => {
        if (startTimeStr) {
          const startTime = parseInt(startTimeStr);
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          setDuration(elapsed);
        }
      }).catch(() => {});

      // Real-time sniff time: accumulated completed periods + any
      // ongoing sniff period detected by speed < threshold
      if (stopStartTimeRef.current) {
        const activeStopSeconds = Math.floor((Date.now() - stopStartTimeRef.current) / 1000);
        setSniffTime(sniffTimeRef.current + activeStopSeconds);
      }
    }, 1000);
  };

  useEffect(() => { if (duration > 0) setPace(computePace(duration, coordinates)); }, [duration, coordinates]);

  // Recording but zero GPS points after 90s? Surface a manual diagnostics
  // share (system Alert dialogs have proven unreliable on this S25/One UI 9
  // firmware, so this is a plain touchable, not a dialog).
  useEffect(() => {
    if (!isRecording) return;
    const iv = setInterval(() => {
      if (!gpsCtaShownRef.current && duration >= 90 && coordinatesRef.current.length === 0) {
        gpsCtaShownRef.current = true;
        diag('zero_points_after_90s', `dur=${duration}`);
        setShowGpsCta(true);
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [isRecording, duration]);

  const shareDiagnostics = async () => {
    try {
      const dump = await collectDiagnosticDump();
      await Share.share({ message: dump, title: 'TailTrails GPS diagnostics' });
      diag('diag_shared');
    } catch (err) {
      console.warn('share diagnostics failed', err);
    }
  };

  const initialTrailCoordinates: Coordinate[] | undefined = initialTrail
    ? (initialTrail.coordinates && initialTrail.coordinates.length > 0
      ? initialTrail.coordinates
      : initialTrail.path && initialTrail.path.length > 0
        ? initialTrail.path.map((p: any) => ({ latitude: p[1], longitude: p[0] }))
        : undefined)
    : undefined;

  const distance = calculateTotalDistance(coordinates);

  const progress = initialTrail && isRecording && coordinates.length > 0 && initialTrailCoordinates && initialTrailCoordinates.length > 0
    ? Math.min((distance / (initialTrail.distance || distance)) * 100, 100)
    : 0;

  const showProgress = !!initialTrail;

  const recenterMap = makeRecenterMapFactory({ mapRef, bgReadyRef: bgReady, currentLocationRef: { current: currentLocation }, setCurrentLocation, setAccuracy, coordinatesRef });

  const handleCamera = async () => {
    try {
      const result = await captureAndStoreRecordingPhoto();
      if (result.status === 'not-supported') {
        Alert.alert(t('record.notSupported'), t('record.cameraNotSupportedWeb'));
        return;
      }
      if (result.status === 'permission-denied') {
        Alert.alert(t('record.cameraPermissionRequired'), t('record.cameraPermissionRequiredMessage'));
        return;
      }
      if (result.status === 'saved') {
        const message = result.savedToGallery
          ? t('record.photoSavedGallery')
          : t('record.photoSavedRecording');
        Alert.alert(t('record.photoSaved'), message);
      }
    } catch (err) {
      console.error('Camera error', err);
      Alert.alert(t('common.error'), t('record.failedToOpenCamera'));
    }
  };

  // bottom-panel controls (fixed panel — no drag sheet in the redesign;
  // follow.tsx keeps the old useBottomSheet sheet)

  const stopRecording = async () => {
    if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
    const snapshot = await stopRecordingShared({ timerRef, coordinates, maxElevation, maxSpeed, duration });
    const finalCoords = snapshot.coordinates;
    const finalMaxElevation = snapshot.maxElevation;
    const finalMaxSpeed = snapshot.maxSpeed;
    const finalDuration = snapshot.duration;

    const finalSniffTime = stopStartTimeRef.current
      ? sniffTime + Math.floor((Date.now() - stopStartTimeRef.current) / 1000)
      : sniffTime;

    if (finalCoords.length < 2) {
      Alert.alert(t('record.noTrailData'), t('record.noTrailDataMessage'));
      setIsRecording(false);
      setCoordinates([]);
      setDuration(0);
      await clearRecordingSession();
      return;
    }

    const distance = calculateTotalDistance(finalCoords);
    const finalPace = computePace(finalDuration, finalCoords);

    const trail: Trail = {
      id: Date.now().toString(),
      date: Date.now(),
      distance,
      duration: finalDuration,
      sniffTime: finalSniffTime,
      coordinates: finalCoords,
      trailPoints: finalCoords,
      city: startLocation?.city,
      country: startLocation?.country,
      pace: finalPace,
      speed: finalMaxSpeed,
      maxElevation: finalMaxElevation,
    };

    try {
      const draftId = await saveEndWalkDraft(trail);
      router.push(`/end-walk/summary?draftId=${draftId}`);
    } catch (err) {
      console.error('Failed to navigate to end-walk summary:', err);
    }
  };

  const cancelRecording = async () => {
    Alert.alert(t('record.cancelRecording'), t('record.cancelRecordingMessage'), [
      { text: t('record.keepRecording'), style: 'cancel' },
      {
        text: t('record.discard'), style: 'destructive', onPress: async () => {
          logEvent('walk_cancelled');
          try { if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium); await BackgroundGeolocation.stop(); } catch (err) { console.warn('Error stopping background task on cancel:', err); }
          if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
          try { await clearRecordingSession(); } catch (err) { console.warn('Error clearing recording storage on cancel:', err); }
          // Reset sniff time
          setIsRecording(false); setCoordinates([]); setDuration(0); setMaxElevation(0); setMaxSpeed(0); setSniffTime(0); sniffTimeRef.current = 0; stopStartTimeRef.current = null;
        }
      }
    ]);
  };

  if (isLoadingPermission && true) {
    return (
      <View style={styles.loadingContainer}>
        <LottieLoader size={120} />
      </View>
    );
  }

  if (!hasPermission) {
    console.log('No location permission');
    return (
      <View style={styles.permissionContainer}>
        <MapPin size={64} color="#666" />
        <Text style={styles.permissionTitle}>{t('record.locationPermissionRequired')}</Text>
        <Text style={styles.permissionText}>{t('record.locationPermissionMessage')}</Text>
        <TouchableOpacity style={styles.permissionButton} onPress={() => { if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium); requestPermissions(); }}>
          <Text style={styles.permissionButtonText}>{t('record.grantPermission')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {initialTrailCoordinates && (
        <TrailMap
          coordinates={initialTrailCoordinates}
          style={styles.map}
          showOnlyPath
          showsUserLocation={false}
          followsUserLocation={false}
          routeColor={theme.accentSecondary}
          routeOpacity={0.75}
        />
      )}

      {/* Removing watch dog notifications for now */}
      {/* <LocationWatchdog isRecording={isRecording} thresholdSeconds={12} /> */}

      {(currentLocation || initialTrail) && (
        <TrailMap
          ref={mapRef}
          coordinates={isRecording ? coordinates : (initialTrail ? initialTrailCoordinates ?? initialTrail.coordinates : coordinates)}
          style={styles.map}
          initialRegion={
            currentLocation
              ? { latitude: currentLocation.latitude, longitude: currentLocation.longitude, latitudeDelta: 0.01, longitudeDelta: 0.01 }
              : initialTrailCoordinates && initialTrailCoordinates.length > 0
                ? { latitude: initialTrailCoordinates[0].latitude, longitude: initialTrailCoordinates[0].longitude, latitudeDelta: 0.01, longitudeDelta: 0.01 }
                : undefined
          }
          userLocation={currentLocation}
          showsUserLocation
          followsUserLocation={false}
          routeColor={theme.backgroundPrimary}
          routeWidth={5}
          routeOpacity={1}
          showsMyLocationButton={false}
          onUserLocationUpdate={(coord) => {
            setCurrentLocation(coord);
            if (isRecordingRef.current) {
              // TrailMap gives us a bare lat/lon; add the required
              // `recordedAt` so it's a valid TrailPoint for the path.
              const point: TrailPoint = { ...coord, recordedAt: Date.now() };
              try {
                appendCoordinateToPath(setCoordinates, point, coordinatesRef.current, {
                  accuracy,
                  isSample: false,
                });
              } catch (e) {
                console.warn('Error appending point from TrailMap user location', e);
                appendCoordinateToPath(setCoordinates, point, undefined, {
                  accuracy,
                  isSample: false,
                });
              }
            }
          }}
        />
      )}

      <TouchableOpacity style={styles.recenterButton} onPress={recenterMap} activeOpacity={0.8}>
        <Navigation size={24} color={theme.accentPrimary} />
      </TouchableOpacity>

      {/* Floating camera button (design: WhileRecording, bottom-right) */}
      {isRecording && (
        <TouchableOpacity style={[styles.cameraFab, { bottom: 220 + insets.bottom }]} onPress={handleCamera} activeOpacity={0.8}>
          <Camera size={22} color={theme.accentPrimary} />
        </TouchableOpacity>
      )}

      {startError && (
        <TouchableOpacity
          onPress={shareDiagnostics}
          activeOpacity={0.85}
          style={{
            position: 'absolute', left: 16, right: 16,
            bottom: 268 + insets.bottom, zIndex: 11,
            backgroundColor: theme.backgroundSecondary, borderRadius: 14,
            padding: 14,
          }}
        >
          <Text style={{ color: theme.textPrimary, fontSize: 13 }}>{t('record.startFailedBanner')}</Text>
          <Text style={{ color: theme.textMuted, fontSize: 11, marginTop: 4 }}>{startError}</Text>
        </TouchableOpacity>
      )}

      {showGpsCta && (
        <TouchableOpacity
          onPress={shareDiagnostics}
          activeOpacity={0.85}
          style={{
            position: 'absolute', left: 16, right: 16,
            bottom: 210 + insets.bottom, zIndex: 10,
            backgroundColor: theme.backgroundSecondary, borderRadius: 14,
            padding: 14,
          }}
        >
          <Text style={{ color: theme.textPrimary, fontSize: 13 }}>{t('record.gpsNotReceiving')}</Text>
        </TouchableOpacity>
      )}

      {/* Fixed bottom panel (design: always-visible stats + Cancel/Stop) */}
      <View style={[styles.bottomPanel, { paddingBottom: Math.max(12, insets.bottom + 12) }]}>
        <RecordOverlay
          isRecording={isRecording}
          isExpanded={false}
          duration={duration}
          distance={distance}
          elevation={elevation}
          pace={pace}
          speed={speed}
          progress={progress}
          showProgress={showProgress}
          onStart={startRecording}
          startLabel={t('record.startTrail')}
          accuracy={accuracy}
          sniffDuration={sniffTime}
          onStop={stopRecording}
          onClose={() => { router.back(); }}
          onCancel={cancelRecording}
          onCamera={handleCamera}
        />
      </View>

    </View>
  );
}